// cSpell:ignore alarielle bloodmarked khorne lifespring nurgle

import type { JSX } from "react";
import type {
  Action,
  SpearheadGameRuleState,
  SpearheadRealm,
  SpearheadTerrainFeatureId,
  SpearheadTerrainFeatureSetId,
  TwistCard} from "whole-shared";
import {
  BellTerrainFeature,
  BoneTerrainFeature,
  otherPlayer,
  playerInTurn,
  RavagedShrineBell,
  RavagedShrineBone,
  RuinedDomicileBell,
  RuinedDomicileBone,
  SpearheadGameRule,
  TwistCards
} from "whole-shared";

import { diceRoller } from "../globals";
import { hud } from "../hud";
import { ClientGameRule, sendAction } from "./ClientGameRule";
import type { ClientTerrainFeature, ClientTerrainFeatureSet } from "./ClientTerrainFeature";

const maps: { [key in SpearheadRealm]: { name: string } } = { aqshy: { name: "Aqshy" }, ghyran: { name: "Ghyran" } };

export const ClientRuinedDomicileBell: ClientTerrainFeature = { ...RuinedDomicileBell, name: "Ruined Domicile" };
export const ClientRavagedShrineBell: ClientTerrainFeature = { ...RavagedShrineBell, name: "Ravaged Shrine" };
export const ClientRuinedDomicileBone: ClientTerrainFeature = { ...RuinedDomicileBone, name: "Ruined Domicile" };
export const ClientRavagedShrineBone: ClientTerrainFeature = { ...RavagedShrineBone, name: "Ravaged Shrine" };

export const ClientSpearheadTerrainFeatures: { [key in SpearheadTerrainFeatureId]: ClientTerrainFeature } = {
  ravaged_bell: ClientRavagedShrineBell,
  ravaged_bone: ClientRavagedShrineBone,
  ruined_bell:  ClientRuinedDomicileBell,
  ruined_bone:  ClientRuinedDomicileBone
};

export const ClientBellTerrainFeature: ClientTerrainFeatureSet = { ...BellTerrainFeature, name: "Bell" };
export const ClientBoneTerrainFeature: ClientTerrainFeatureSet = { ...BoneTerrainFeature, name: "Bone" };

export const ClientSpearheadTerrainFeatureSets: { [key in SpearheadTerrainFeatureSetId]: ClientTerrainFeatureSet } = {
  bell: ClientBellTerrainFeature,
  bone: ClientBoneTerrainFeature
};

export interface ClientTwistCard extends TwistCard {
  description: JSX.Element;
  name: string;
}

export const ClientTwistCards: { [key in SpearheadRealm]: ClientTwistCard[] } = {
  aqshy: [
    {
      ...TwistCards.aqshy[0],
      description: (
        <>
          <i>Khorne&apos;s presence is felt in this battle, and he demands blood be spilled in his name!</i>
          <br />
          <br />
          Each player scores <i>1 extra victory point</i> at the end of their turn for each enemy unit destroyed that turn.
          <br />
          <br />
          In addition, if one of the players is the <i>underdog</i>, that player picks 1 friendly unit on the battlefield to be <i>frenzied</i>. Add 1
          to hit rolls for combat attacks made by that unit this battle round.
        </>
      ),
      name: "LET THE BLOOD FLOW"
    },
    {
      ...TwistCards.aqshy[1],
      description: (
        <>
          <i>A fire is stoked in the hearts of the embattled warriors, giving them the courage to push deep into enemy lines.</i>
          <br />
          <br />
          Each player scores <i>1 extra victory point</i> at the end of their turn for each objective they control that was controlled by the opponent
          at the start of their turn.
          <br />
          <br />
          In addition, the <i>underdog</i> picks 1 friendly unit to be the spearhead. That unit&apos;s melee weapons have <i>Charge (1+ Damage)</i>{" "}
          this battle round.
        </>
      ),
      name: "MOUNT THE ATTACK"
    },
    {
      ...TwistCards.aqshy[2],
      description: (
        <>
          The <i>underdog</i> pics 1 objective on the battlefield. If there is no <i>underdog</i>, the players roll off and the winner picks the
          objective.
          <br />
          <br />
          That objective is no longer controlled by either player and cannot be contested or controlled in this battle round.
          <br />
          In addition, at the end of the turn in this battle round, after victory points have been scored, inflict D3 mortal damage on each unit that
          has any models on it (roll for each unit).
        </>
      ),
      name: "RING OF FIRE"
    },
    {
      ...TwistCards.aqshy[3],
      description: (
        <>
          <i>Blood is spilled relentlessly in the pursuit of conquering this battle-scarred realm.</i>
          <br />
          <br />
          The <i>underdog</i> picks 2 objectives on the battlefield.
          <br />
          <br />
          If there is no <i>underdog</i>, each player picks 1 objective on the battlefield, starting with the active player.
          <br />
          <br />
          Each player scores 1 extra victory point at the end of their turn for each of those objectives they control.
        </>
      ),
      name: "RECLAIM AQSHY"
    },
    {
      ...TwistCards.aqshy[4],
      description: (
        <>
          <i>Plumes of thick black smoke erupt f rom the scarred land, obscuring the warriors in battle.</i>
          <br />
          <br />
          The <i>underdog</i> picks 1 objective on the battlefield. If there is no <i>underdog</i>, the players roll off and the winner picks the
          objective.
          <br />
          <br />
          That objective is wreathed in smoke. Until the end of the battle round, only unmodified hit rolls of 6 successfully hit for attacks that
          target a unit contesting that objective.
        </>
      ),
      name: "WREATHED IN SMOKE"
    },
    {
      ...TwistCards.aqshy[5],
      description: (
        <>
          The <i>underdog</i> picks 1 unit in each player&apos;s army that is on the battlefield to be bloodmarked.
          <br />
          <br />
          If there is no <i>underdog</i>, each player picks 1 enemy unit on the battlefield to be bloodmarked, starting with the active player.
          <br />
          <br />
          Each player scores 1 extra victory point at the end of the battleground if the enemy bloodmarked unit is destroyed this battle round.
        </>
      ),
      name: "BLOODMARKED"
    }
  ],
  ghyran: [
    {
      ...TwistCards.ghyran[0],
      description: (
        <>
          The <i>underdog</i> can pick one of the effects below. If there is no <i>underdog</i>, the players roll off and the winner picks an enemy
          unit on the battlefield.
          <br />
          <br />
          <i>Nurgle&apos;s Rot:</i> Pick an enemy unit on the battlefield. Subtract 1 from save rolls for that unit this battle round.
          <br />
          <br />
          <i>Eroding Miasma:</i> Pick an enemy unit on the battlefield. Ward rolls cannot be made for that unit this battle round.
        </>
      ),
      name: "THE GRANDFATHER'S BLESSING"
    },
    {
      ...TwistCards.ghyran[1],
      description: (
        <>
          <i>To gain a foothold in the Realms of Lif e, one must plant the seeds of victory for future generations.</i>
          <br />
          <br />
          The <i>underdog</i> picks 2 objectives on the battlefield. If there is no <i>underdog</i>, each player picks 1 objective on the battlefield,
          starting with the active player.
          <br />
          <br />
          Each player scores <i>1 extra victory point</i> at the end of their turn for each of those objectives that they control.
        </>
      ),
      name: "RECLAIM GHYRAN"
    },
    {
      ...TwistCards.ghyran[2],
      description: (
        <>
          <i>The twisting vines burst from the undergrowth to ensnare hapless warriors.</i>
          <br />
          <br />
          The <i>underdog</i> picks an enemy unit on the battlefield. If there is no <i>underdog</i>, the players roll off and the winner picks an
          enemy unit on the battlefield.
          <br />
          <br />
          That unit is ensnared. Until the end of the battle round, halve its Move characteristic and roll 1 fewer dice when making a charge roll for
          that unit.
        </>
      ),
      name: "GRASPING VINES"
    },
    {
      ...TwistCards.ghyran[3],
      description: (
        <>
          <i>The verdant land of Ghyran is rich in natural resources waiting to be claimed.</i>
          <br />
          <br />
          Each player scores <i>1 extra victory point</i> at the end of their turn if there are more friendly models contesting the large terrain
          feature in enemy territory and there are no enemy models contesting it.
          <br />
          <br />
          In addition, the <i>underdog</i> adds 1 to wound rolls for attacks made by friendly units that target a unit contesting a{" "}
          <i>large terrain feature</i>.
        </>
      ),
      name: "TAKE THE LAND"
    },
    {
      ...TwistCards.ghyran[4],
      description: (
        <>
          The <i>underdog</i> picks 1 objective on the battlefield. If there is no <i>underdog</i>, the players roll off and the winner picks an
          objective on the battlefield.
          <br />
          <br />
          That objective becomes a <i>lifespring</i>, this battle round. Each player scores 1 extra victory point at the end of their turn if they
          control that objective.
          <br />
          <br />
          In addition, at the end of each turn in this battle round, after victory points have been scored, <i>Heal (1)</i> each unit that is
          contesting that objective.
        </>
      ),
      name: "LIFESPRING"
    },
    {
      ...TwistCards.ghyran[5],
      description: (
        <>
          The <i>underdog</i> can pick one of the effects below. If there is no <i>underdog</i>, the players roll off and the winner can pick the
          effect.
          <br />
          <br />
          <i>Shield of Thorns:</i> Pick a friendly unit on the battlefield. That unit has WARD (6+) this battle round. If this unit already has a ward
          instead add 1 to its ward save for this battle round instead.
          <br />
          <br />
          <i>Rain of Jade:</i> Pick up to 3 different friendly units on the battlefield. Heal (D3) each of those units (roll for each).
        </>
      ),
      name: "ALARIELLE'S BLESSING"
    }
  ]
};

export class ClientSpearheadGameRule extends SpearheadGameRule<false>(ClientGameRule<SpearheadGameRuleState>) {
  name = "Age of Sigmar v4 - Spearhead";

  action(playerId: number, action: Action) {
    const { map, twistCard, underdog } = this.state;

    hud.setHeader("");
    hud.underdog(underdog);
    hud.twistCard(twistCard === undefined ? undefined : ClientTwistCards[map.realm][twistCard]);

    return super.action(playerId, action);
  }

  async begin(playerId: number, _action: Action) {
    hud.phase("Roll off");

    const rollOffResult = await this.rollOff(playerId, {});

    if(rollOffResult === -1) return;

    const players = [0, this.player1Id, this.player2Id];
    const { firstTurn, map, terrainSets, turn } = this.state;

    if(rollOffResult !== 0) {
      hud.phase("Role choice");
      hud.description(`Commander ${rollOffResult} has to choose role`);

      if(playerId !== players[rollOffResult]) return;

      hud.addChoice("Attacker", sendAction({ role: 1 }));
      hud.addChoice("Defender", sendAction({ role: 2 }));

      return;
    }

    diceRoller.setState({ hidden: true });

    const inTurn = playerInTurn[firstTurn][turn];
    const other = otherPlayer(inTurn);

    if(this.setEnhancements()) {
      hud.phase("Enhancements choice");
      hud.armiesSet(true);
      hud.description(`Commander ${inTurn} has to choose enhancements`);

      return;
    }

    if(this.setMap()) {
      hud.phase("Map choice");

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if(! map.realm) {
        hud.description(`Commander ${other} has to choose the realm`);

        if(playerId === players[other]) {
          hud.addChoice(maps.ghyran.name, sendAction({ map: { realm: "ghyran" } }));
          hud.addChoice(maps.aqshy.name, sendAction({ map: { realm: "aqshy" } }));
        }

        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if(map.side === undefined) {
        hud.description(`Commander ${other} has to choose their side`);

        if(playerId === players[other]) {
          hud.addChoice("Up", sendAction({ map: { side: inTurn === 1 } }));
          hud.addChoice("Bottom", sendAction({ map: { side: inTurn === 2 } }));
        }

        return;
      }

      hud.description(`Commander ${other} has to choose the map`);

      if(playerId === players[other]) {
        hud.addChoice("Diagonal", sendAction({ map: { horizontal: false } }));
        hud.addChoice("Horizontal", sendAction({ map: { horizontal: true } }));
      }

      return;
    }

    if(this.setTerrainFeatures()) {
      hud.phase("Terrain Features");

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if(! terrainSets) {
        hud.description(`Commander ${other} has to choose their ones`);

        if(playerId === players[other]) {
          hud.addChoice(ClientSpearheadTerrainFeatureSets.bone.name, sendAction({ terrainSet: "bone" }));
          hud.addChoice(ClientSpearheadTerrainFeatureSets.bell.name, sendAction({ terrainSet: "bell" }));
        }

        return;
      }

      hud.description(`Commander ${other} has to set up their ones`);

      if(playerId === players[other])
        hud.addChoice("Ok", sendAction({ terrainFeatures: this.otherTerrainFeatures(terrainSets, other).map(id => ({ id })) }));

      return;
    }

    hud.phase("Deploy");
    hud.description(`Commander ${inTurn} has to set deploy their army`);

    if(playerId === players[inTurn]) hud.addChoice("Deploy", sendAction({ deploy: true }));

    return;
  }

  async round(playerId: number, _action: Action) {
    const players = [0, this.player1Id, this.player2Id];
    const { firstTurn, stage } = this.state;

    hud.phase("Round start");

    if(stage === "firstTurn") {
      hud.description(`Commander ${firstTurn} has to choose who is first in turn`);

      if(playerId !== players[firstTurn]) return;

      hud.addChoice("Commander 2", sendAction({ role: 2 }));
      hud.addChoice("Commander 1", sendAction({ role: 1 }));

      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if(stage === "twist") {
      const rollOffResult = await this.rollOff(playerId, {});

      if(rollOffResult === -1) return;
    }
  }
}
